import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { validateHetu } from 'common/src/vtj/validation/validators-typed'
import type { Huollettava } from 'todistukset-common/src/vtj/vtj-kysely/huoltod-soap-response-converter'
import fp from 'lodash/fp'
import { ApostilleCountries } from 'todistukset-common/src/vtj/ApostilleCountries.enum'
import { getEnabledFeatures } from 'todistukset-common/src/vtj/certificate.util'
import { CertificateFeature } from 'todistukset-common/src/vtj/CertificateFeature.enum'
import CertificateSubtypes from 'todistukset-common/src/vtj/CertificateSubtypes.enum'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import {
  getValtioKoodistoEntryByCode,
  isCountryInApostilleByCode,
  isCountryInEUByCode,
} from 'todistukset-common/src/vtj/country.util'
import { isDependantRestricted } from 'todistukset-common/src/vtj/dependant-helpers'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import { checkIfMailDeliveryIsEnabled } from 'todistukset-common/src/vtj/form-check.util'
import {
  normalizeTextInputWhitespace,
  REGEX_MAIL_DELIVERY_NOT_ALLOWED_CHARACTERS,
} from 'todistukset-common/src/vtj/form-sanitization.util'
import {
  availableTodistusAsiointikielet,
  availableTranslations,
  getAvailableTodistusLanguages,
} from 'todistukset-common/src/vtj/language.util'
import { isValueInEnum } from 'todistukset-common/src/vtj/type-guards'
import type {
  ContactInfo,
  EidasOrderer,
  HetuOrderer,
  MailDelivery,
  Orderer,
  OrdererExtraIdentification,
  StoreUiTodistusPyyntö,
  UsageRegion,
  ValidatorTodistusPyyntö,
} from 'todistukset-common/src/vtj/types/todistus'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { UsageRegionReceiver } from 'todistukset-common/src/vtj/UsageRegionReceiver.enum'
import { UsageRegionTarget } from 'todistukset-common/src/vtj/UsageRegionTarget.enum'
import {
  getOrdererFromUser,
  isEidasIdentifiedUser,
  isSameHetuOrEidasPID,
} from 'todistukset-common/src/vtj/user.utils'
import {
  type CountryKoodistoCode,
  isCountryKoodistoCode,
  VtjCountryCodeAdditions,
} from 'todistukset-common/src/vtj/vtj-koodisto.util'
import { z, ZodIssueCode } from 'zod'
import {
  TodistusPersonHetuSource,
  TodistusPersonRole,
  TodistusPersonType,
  UnpersistedTodistusPerson,
} from 'todistukset-common/src/vtj/types/todistus-person.type'
import {
  finalEditableFieldValue,
  IncludeApostille,
  isEditableField,
  TodistusKieli,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { matches } from 'lodash'
import { tilaustenMaaraSchema } from 'todistukset-common/src/vtj/schemas/subjectSchema'

type KeysOfUnion<T> = T extends T ? keyof T : never

const optionalFormKeys: KeysOfUnion<ValidatorTodistusPyyntö>[] = [
  'paymentId',
  'ordererExtraIdentification',
  'ordererExtraIdentificationOptions',
  'source',
  'diaariId',
]

const expectedMailDeliveryKeys: (keyof MailDelivery)[] = [
  'firstName',
  'lastName',
  'streetAddress',
  'postOffice',
  'postalCode',
  'country',
  'streetAddressExtras',
]

const expectedContactInfoKeys: (keyof ContactInfo)[] = ['email', 'phoneNumber']

const expectedUsageRegionKeys: (keyof UsageRegion)[] = [
  'target',
  'countryKoodistoCode',
  'receiver',
]

const expectedOrdererExtraIdentificationKeys: (keyof OrdererExtraIdentification)[] =
  [
    'birthPlace',
    'birthCountry',
    'finnishHetu',
    'foreignPersonId',
    'foreignPersonIdCountry',
    'previousName',
  ]

export const MAX_PREVIOUS_ADDRESSES_OVER_YEARS = 999

const MAX_LENGTH_OF_MAIL_DELIVERY_FIELD = 100

export type ErrorTranslationParams = { [k: string]: string | number }

export type ValidationError = {
  propertyName: string
  errorTranslationKey: string
  errorTranslationParams?: ErrorTranslationParams
}

type ValueValidatorOld<Obj = ValidatorTodistusPyyntö> = (
  value: unknown,
  form: Obj,
  user: TodistuksetUser
) => boolean

export type ValueValidator<Obj = ValidatorTodistusPyyntö> = (
  value: unknown,
  form: Obj,
  user: TodistuksetUser
) => undefined | ValidationError

export type ValueValidatorCommon<Obj = ValidatorTodistusPyyntö> =
  | ValueValidator<Obj>
  | ValueValidatorOld<Obj>

type HasSameKeys<Obj> = { [TKeys in keyof Obj]: unknown }

type Validation<Obj> = [string, ValueValidatorCommon<Obj>]

type TodistusPyyntöValueValidatorOld = ValueValidatorOld<
  HasSameKeys<ValidatorTodistusPyyntö>
>
type TodistusPyyntöValueValidator = ValueValidator<
  HasSameKeys<ValidatorTodistusPyyntö>
>

export const validateFormAndGetError = (
  form: unknown,
  user: TodistuksetUser
): string | null => {
  if (!validateFormRoot(form)) {
    return `Form validation failed: Root value is invalid.`
  }

  const baseValidations: Validation<HasSameKeys<ValidatorTodistusPyyntö>>[] = [
    ['todistusType', validateTodistusType],
    ['todistusLanguage.ilmoitettuArvo', validateTodistusLanguage],
    ['translationFormLanguage.ilmoitettuArvo', validateTranslationFormLanguage],
    [
      'previousAddressesOverYears.ilmoitettuArvo',
      validatePreviousAddressesOverYears,
    ],
    ['includeHetu.ilmoitettuArvo', validateIncludeHetu],
    ['includePreviousNames.ilmoitettuArvo', validateIncludePreviousNames],
    [
      'includeSpousePreviousNames.ilmoitettuArvo',
      validateIncludeSpousePreviousNames,
    ],
    ['includeMaritalStatus.ilmoitettuArvo', validateIncludeMaritalStatus],
    [
      'includePreviousPermanentAddresses.ilmoitettuArvo',
      validateIncludePreviousPermanentAddresses,
    ],
    [
      'includePreviousMarriages.ilmoitettuArvo',
      validateIncludePreviousMarriages,
    ],
    ['includeNationality.ilmoitettuArvo', validateIncludeNationality],
    ['includePermanentAddress.ilmoitettuArvo', validateIncludePermanentAddress],
    [
      'includePreviousTemporaryAddresses.ilmoitettuArvo',
      validateIncludePreviousTemporaryAddresses,
    ],
    [
      'includePreviousKotikunta.ilmoitettuArvo',
      validateIncludePreviousKotikunta,
    ],
    ['includeKotikunta.ilmoitettuArvo', validateIncludeKotikunta],
    ['includeParentsHetu.ilmoitettuArvo', validateIncludeParentsHetu],
    [
      'includeParentsPreviousNames.ilmoitettuArvo',
      validateIncludeParentsPreviousNames,
    ],
    [
      'includeNumberOfMarriages.ilmoitettuArvo',
      validateIncludeNumberOfMarriages,
    ],
    ['includeTranslationForm.ilmoitettuArvo', validateIncludeTranslationForm],
    ['mailDelivery', validateMailDeliveryRoot],
    ['mailDelivery.firstName', validateMailDeliveryFirstName],
    ['mailDelivery.lastName', validateMailDeliveryLastName],
    ['mailDelivery.streetAddress', validateMailDeliveryStreetAddress],
    ['mailDelivery.postOffice', validateMailDeliveryPostOffice],
    ['mailDelivery.postalCode', validateMailDeliveryPostalCode],
    ['mailDelivery.country', validateMailDeliveryCountry],
    [
      'mailDelivery.streetAddressExtras',
      validateMailDeliveryStreetAddressExtras,
    ],
    ['contactInfo', validateContactInfoRoot],
    ['contactInfo.email.ilmoitettuArvo', validateContactInfoEmail],
    ['contactInfo.phoneNumber.ilmoitettuArvo', validateContactInfoPhoneNumber],
    ['orderer', validateOrderer],
    ['persons', validatePersons],
    ['paymentId', isPaymentId],
  ]

  const apostilleValidations: Validation<
    HasSameKeys<ValidatorTodistusPyyntö>
  >[] = [
    ['todistusSubtype', validateTodistusSubtype],
    ['usageRegion', validateUsageRegion],
    ['usageRegion.countryKoodistoCode', validateUsageRegionCountry],
    ['usageRegion.target', validateUsageRegionTarget],
    ['usageRegion.receiver', validateUsageRegionReceiver],
    ['includeApostille.ilmoitettuArvo', validateIncludeApostille],
  ]

  const eidasValidations: Validation<HasSameKeys<ValidatorTodistusPyyntö>>[] = [
    ['ordererExtraIdentification', validateOrdererExtraIdentification],
    [
      'ordererExtraIdentification.birthPlace',
      validateOrdererExtraIdentificationBirthPlace,
    ],
    [
      'ordererExtraIdentification.birthCountry',
      validateOrdererExtraIdentificationBirthCountry,
    ],
    [
      'ordererExtraIdentification.finnishHetu',
      validateOrdererExtraIdentificationFinnishHetu,
    ],
    [
      'ordererExtraIdentification.foreignPersonId',
      validateOrdererExtraIdentificationForeignPersonId,
    ],
    [
      'ordererExtraIdentification.foreignPersonIdCountry',
      validateOrdererExtraIdentificationForeignPersonIdCountry,
    ],
    [
      'ordererExtraIdentification.previousName',
      validateOrdererExtraIdentificationPreviousName,
    ],
  ]

  const validations = [
    ...apostilleValidations,
    ...baseValidations,
    ...eidasValidations,
  ]

  const optionalKeysObj = optionalFormKeys.reduce<Record<string, true>>(
    (current, value) => {
      current[value] = true
      return current
    },
    {}
  )

  const failedValidation = fp.find(([property, validator]) => {
    const value = fp.get(property, form)
    if (optionalKeysObj[property] && fp.isUndefined(value)) {
      return false
    }
    const validationResult = validator(value, form, user)
    if (typeof validationResult === 'boolean') {
      return validationResult === false
    }

    return validationResult !== undefined
  }, validations)

  if (failedValidation) {
    return `Form validation failed: '${failedValidation[0]}' is invalid.`
  }

  return null
}

export const isApostilleOrderForm = (
  form: HasSameKeys<ValidatorTodistusPyyntö>
): form is HasSameKeys<ValidatorTodistusPyyntö> & {
  todistusSubtype: CertificateSubtypes.APOSTILLE
  includeApostille: IncludeApostille &
    ({ ilmoitettuArvo: true } | { virkailjaArvo: true })
} => {
  return (
    form.todistusSubtype === CertificateSubtypes.APOSTILLE &&
    isEditableField(form.includeApostille) &&
    finalEditableFieldValue(form.includeApostille) === true
  )
}

const expectedFormKeys: (keyof ValidatorTodistusPyyntö)[] = [
  'todistusType',
  'todistusSubtype',
  'todistusLanguage',
  'translationFormLanguage',
  'previousAddressesOverYears',
  'usageRegion',
  'includeApostille',
  'includeHetu',
  'includePreviousNames',
  'includeSpousePreviousNames',
  'includeMaritalStatus',
  'includePreviousPermanentAddresses',
  'includePreviousMarriages',
  'includeNationality',
  'includePermanentAddress',
  'includePreviousTemporaryAddresses',
  'includePreviousKotikunta',
  'includeKotikunta',
  'includeParentsHetu',
  'includeParentsPreviousNames',
  'includeNumberOfMarriages',
  'includeTranslationForm',
  'mailDelivery',
  'contactInfo',
  'orderer',
  'persons',
]
export const validateFormRoot = (
  form: unknown
): form is HasSameKeys<ValidatorTodistusPyyntö> => {
  return hasSameKeys(form, expectedFormKeys, optionalFormKeys)
}

export const validateTodistusType: TodistusPyyntöValueValidatorOld = (value) =>
  isValueInEnum(value, CertificateTypes)

export const validateTodistusSubtype: TodistusPyyntöValueValidatorOld = (
  value,
  form,
  user
) => {
  if (!isValueInEnum(value, CertificateSubtypes)) {
    return false
  }

  if (
    value === CertificateSubtypes.SAHKOINEN &&
    isMailDeliveryEnabled(form.usageRegion, getOrdererFromUser(user))
  ) {
    return false
  }

  return true
}

export const validateTodistusLanguage: TodistusPyyntöValueValidatorOld = (
  value,
  form
) => {
  const usageRegionCountryCode =
    isUsageRegion(form.usageRegion) &&
    isCountryKoodistoCode(form.usageRegion.countryKoodistoCode)
      ? form.usageRegion.countryKoodistoCode
      : null
  const validValues = getAvailableTodistusLanguages(usageRegionCountryCode)
  return fp.includes(value, validValues)
}

export const validateTranslationFormLanguage: TodistusPyyntöValueValidatorOld =
  (value, form) => {
    if (
      isEditableField(form.includeTranslationForm) &&
      !finalEditableFieldValue(form.includeTranslationForm) &&
      value === null
    ) {
      return true
    }
    return fp.includes(value, availableTranslations)
  }

export const validatePreviousAddressesOverYears: TodistusPyyntöValueValidatorOld =
  (value) => {
    return (
      value === null ||
      (typeof value === 'number' &&
        isWithinBounds(value, 1, MAX_PREVIOUS_ADDRESSES_OVER_YEARS) &&
        Number.isInteger(value))
    )
  }

export const validateUsageRegionCountry: TodistusPyyntöValueValidatorOld = (
  value,
  form
) => {
  if (!hasUsageRegionKeys(form.usageRegion)) {
    return false
  }

  if (value !== null && !isKoodistoCountryCode(value)) {
    return false
  }

  if (isValueInEnum(value, VtjCountryCodeAdditions)) {
    return false // The additions are not actual countries.
  }

  if (
    form.usageRegion.target === UsageRegionTarget.TARGET_COUNTRY &&
    value === null
  ) {
    return false
  }

  if (
    form.usageRegion.target === UsageRegionTarget.EU_AUTHORITY &&
    value !== EuCountries.FINLAND
  ) {
    return false
  }

  if (isApostilleOrderForm(form)) {
    return value !== EuCountries.FINLAND && isCountryInApostilleByCode(value)
  }

  return true
}

export const validateUsageRegionTarget: TodistusPyyntöValueValidatorOld = (
  value,
  form
) => {
  if (isApostilleOrderForm(form)) {
    return value === UsageRegionTarget.TARGET_COUNTRY
  }

  if (hasUsageRegionKeys(form.usageRegion)) {
    return isValueInEnum(value, UsageRegionTarget)
  }

  return true
}

export const validateUsageRegionReceiver: TodistusPyyntöValueValidatorOld = (
  value,
  form
) => {
  if (!hasUsageRegionKeys(form.usageRegion)) {
    return false
  }

  const { target, countryKoodistoCode } = form.usageRegion

  if (!isValueInNullableEnum(value, UsageRegionReceiver)) {
    return false
  }

  if (
    value === null &&
    isKoodistoCountryCode(countryKoodistoCode) &&
    target === UsageRegionTarget.TARGET_COUNTRY &&
    isCountryInEUByCode(countryKoodistoCode) &&
    countryKoodistoCode !== ApostilleCountries.FINLAND
  ) {
    return false
  }

  return true
}

export const validateUsageRegion: TodistusPyyntöValueValidatorOld = (value) => {
  if (hasUsageRegionKeys(value)) {
    return isValueInEnum(value.target, UsageRegionTarget)
  }

  return false
}

const getIncludeValidator = (
  todistusFeature: CertificateFeature,
  options?: { allowedValues?: Array<unknown> }
): TodistusPyyntöValueValidatorOld => {
  return (value, form, user) => {
    if (typeof value !== 'boolean') {
      return false
    }

    if (options?.allowedValues && !options.allowedValues.includes(value)) {
      return false
    }

    if (!user) {
      return false
    }

    if (!value) {
      return true
    }

    if (!isValueInEnum(form.todistusType, CertificateTypes)) {
      return false
    }

    const enabledFeatures = getEnabledFeatures({
      certificateType: form.todistusType,
      isAdult: user.isAdult,
    })

    return fp.includes(todistusFeature, enabledFeatures)
  }
}

export const validateIncludeHetu = getIncludeValidator(
  CertificateFeature.INCLUDE_HETU
)
export const validateIncludePreviousNames = getIncludeValidator(
  CertificateFeature.INCLUDE_PREVIOUS_NAMES
)
export const validateIncludeSpousePreviousNames = getIncludeValidator(
  CertificateFeature.INCLUDE_SPOUSE_PREVIOUS_NAMES
)
export const validateIncludeMaritalStatus = getIncludeValidator(
  CertificateFeature.INCLUDE_MARITAL_STATUS
)
export const validateIncludePreviousPermanentAddresses = getIncludeValidator(
  CertificateFeature.INCLUDE_PREVIOUS_PERMANENT_ADDRESSES
)
export const validateIncludePreviousMarriages = getIncludeValidator(
  CertificateFeature.INCLUDE_PREVIOUS_MARRIAGES
)
export const validateIncludeNationality = getIncludeValidator(
  CertificateFeature.INCLUDE_NATIONALITY
)
export const validateIncludePermanentAddress = getIncludeValidator(
  CertificateFeature.INCLUDE_PERMANENT_ADDRESS
)
export const validateIncludePreviousTemporaryAddresses = getIncludeValidator(
  CertificateFeature.INCLUDE_PREVIOUS_TEMPORARY_ADDRESSES
)
export const validateIncludePreviousKotikunta = getIncludeValidator(
  CertificateFeature.INCLUDE_PREVIOUS_KOTIKUNTA
)
export const validateIncludeKotikunta = getIncludeValidator(
  CertificateFeature.INCLUDE_KOTIKUNTA
)
export const validateIncludeParentsHetu = getIncludeValidator(
  CertificateFeature.INCLUDE_PARENTS_HETU,
  { allowedValues: [false] }
)
export const validateIncludeParentsPreviousNames = getIncludeValidator(
  CertificateFeature.INCLUDE_PARENTS_PREVIOUS_NAMES
)
export const validateIncludeNumberOfMarriages = getIncludeValidator(
  CertificateFeature.INCLUDE_NUMBER_OF_MARRIAGES,
  { allowedValues: [false] }
)

export const validateIncludeTranslationForm: TodistusPyyntöValueValidatorOld = (
  value,
  form,
  user
) => {
  const { usageRegion } = form

  if (value) {
    if (form.todistusSubtype === CertificateSubtypes.SAHKOINEN) {
      return false
    }

    if (isApostilleOrderForm(form)) {
      return false
    }

    if (
      isUsageRegion(usageRegion) &&
      !isCountryInEUByCode(usageRegion.countryKoodistoCode)
    ) {
      return false
    }

    if (
      finalEditableFieldValue(form.todistusLanguage as TodistusKieli) ===
      LanguageCode.EN
    ) {
      return false
    }
  }

  return getIncludeValidator(CertificateFeature.INCLUDE_TRANSLATION)(
    value,
    form,
    user
  )
}

export const validateIncludeApostille: TodistusPyyntöValueValidatorOld = (
  value,
  form,
  user
) => {
  const includeApostille = getIncludeValidator(
    CertificateFeature.INCLUDE_APOSTILLE
  )

  if (value === true) {
    if (!isUsageRegion(form.usageRegion)) {
      return false
    }

    if (form.todistusSubtype !== CertificateSubtypes.APOSTILLE) {
      return false
    }

    const { countryKoodistoCode, receiver } = form.usageRegion
    if (
      !isMailDeliveryEnabled(form.usageRegion, getOrdererFromUser(user)) ||
      !isCountryInApostilleByCode(countryKoodistoCode) ||
      (isCountryInEUByCode(countryKoodistoCode) &&
        receiver === UsageRegionReceiver.TARGET_COUNTRY_AUTHORITY)
    ) {
      return false
    }
  }
  if (
    value !== true &&
    form.todistusSubtype === CertificateSubtypes.APOSTILLE
  ) {
    return false
  }

  return includeApostille(value, form, user)
}

export const validateMailDeliveryParam = ({
  value,
  defaultValue,
  form,
  user,
  propertyKey,
  isOptional,
}: {
  value: unknown
  defaultValue: string
  form: HasSameKeys<ValidatorTodistusPyyntö>
  user: TodistuksetUser
  propertyKey: keyof MailDelivery
  isOptional: boolean
}): ValidationError | undefined => {
  const propertyName = `${
    'mailDelivery' satisfies keyof ValidatorTodistusPyyntö
  }.${propertyKey}`

  if (!isMailDeliveryEnabled(form.usageRegion, getOrdererFromUser(user))) {
    return undefined
  }

  if (typeof value !== 'string' || !value.length) {
    if (isOptional) {
      return undefined
    } else {
      return {
        propertyName,
        errorTranslationKey: 'validationMailDeliveryRequiredValueMissing',
      }
    }
  }

  if (value === defaultValue) {
    return undefined
  }

  if (!isStringOfLength(value, 1, MAX_LENGTH_OF_MAIL_DELIVERY_FIELD)) {
    return {
      propertyName,
      errorTranslationKey: 'validationMailDeliveryLengthExceedsMaximum',
      errorTranslationParams: { count: MAX_LENGTH_OF_MAIL_DELIVERY_FIELD },
    }
  }

  function getInvalidCharacters(value: string) {
    return fp.uniq(value.match(REGEX_MAIL_DELIVERY_NOT_ALLOWED_CHARACTERS))
  }

  const invalidCharacters = getInvalidCharacters(value)
  if (invalidCharacters.length) {
    return {
      propertyName,
      errorTranslationKey: 'validationMailDeliveryContainsInvalidCharacters',
      errorTranslationParams: { characters: invalidCharacters.join(' ') },
    }
  }
}

const getMailDeliveryValidator = (
  mailDeliveryKey: keyof MailDelivery,
  {
    defaultValueUserKey = null,
    isOptional = false,
  }: {
    defaultValueUserKey?: keyof TodistuksetUser | null
    isOptional?: boolean
  } = {}
): TodistusPyyntöValueValidator => {
  return (value, form, user) => {
    return validateMailDeliveryParam({
      value: isEditableField(value) ? finalEditableFieldValue(value) : value,
      form,
      user,
      propertyKey: mailDeliveryKey,
      isOptional,
      defaultValue: defaultValueUserKey
        ? String(user[defaultValueUserKey] || '')
        : '',
    })
  }
}

export const validateMailDeliveryFirstName = getMailDeliveryValidator(
  'firstName',
  { defaultValueUserKey: 'firstNames' }
)
export const validateMailDeliveryLastName = getMailDeliveryValidator(
  'lastName',
  { defaultValueUserKey: 'lastName' }
)
export const validateMailDeliveryStreetAddress = getMailDeliveryValidator(
  'streetAddress',
  { defaultValueUserKey: 'streetAddress' }
)
export const validateMailDeliveryStreetAddressExtras = getMailDeliveryValidator(
  'streetAddressExtras',
  { isOptional: true }
)
export const validateMailDeliveryPostOffice = getMailDeliveryValidator(
  'postOffice',
  { defaultValueUserKey: 'postOffice' }
)
export const validateMailDeliveryPostalCode = getMailDeliveryValidator(
  'postalCode',
  { defaultValueUserKey: 'postalCode' }
)
export const validateMailDeliveryCountry = getMailDeliveryValidator('country')

export const validateMailDeliveryRoot = (
  value: unknown
): value is HasSameKeys<MailDelivery> =>
  hasSameKeys(value, expectedMailDeliveryKeys)

const validateContactInfoRoot = (
  value: unknown
): value is HasSameKeys<ContactInfo> =>
  hasSameKeys(value, expectedContactInfoKeys)

export const validateContactInfoEmail: TodistusPyyntöValueValidator = (
  value,
  form,
  user
) => {
  if (!isMailDeliveryEnabled(form.usageRegion, getOrdererFromUser(user))) {
    return undefined
  }

  if (isStringOfLength(value, 0, 0)) {
    return undefined
  }
  const { success } = z.string().email().safeParse(value)
  if (success) {
    return undefined
  }

  return {
    propertyName: `${'contactInfo' satisfies keyof ValidatorTodistusPyyntö}.${
      'email' satisfies keyof ContactInfo
    }`,
    errorTranslationKey: 'validationContactInfoInvalidEmail',
  }
}

export const validateContactInfoPhoneNumber: TodistusPyyntöValueValidator = (
  value,
  form,
  user
) => {
  const propertyName = `${
    'contactInfo' satisfies keyof ValidatorTodistusPyyntö
  }.${'phoneNumber' satisfies keyof ContactInfo}`
  if (!isMailDeliveryEnabled(form.usageRegion, getOrdererFromUser(user))) {
    return undefined
  }

  if (!isStringOfLength(value, 1)) {
    return {
      propertyName,
      errorTranslationKey: 'validationContactInfoMissingPhoneNumber',
    }
  }

  if (typeof value === 'string' && isValidPhoneNumber(value)) {
    return undefined
  }

  return {
    propertyName,
    errorTranslationKey: 'validationContactInfoInvalidPhoneNumber',
  }
}

export const validateOrderer: TodistusPyyntöValueValidator = (
  value,
  _form,
  user
) => {
  const validationError = {
    propertyName: 'orderer' as keyof ValidatorTodistusPyyntö,
    errorTranslationKey: 'validationOrdererNotMatchingSession',
  }

  const referenceOrderer: HetuOrderer | EidasOrderer = getOrdererFromUser(user)
  const referenceOrdererKeys = Object.keys(referenceOrderer)
  if (!hasSameKeys(value, referenceOrdererKeys)) {
    return validationError
  }

  if (
    referenceOrdererKeys.every((key): boolean =>
      fp.isEqual(referenceOrderer[key as keyof Orderer], value[key])
    )
  ) {
    return undefined
  }
  return validationError
}
const nimiSchema = z.string().min(1).max(100).nullable()

const etunimetSchema = z
  .object({
    vtjArvo: nimiSchema,
    ilmoitettuArvo: nimiSchema,
    virkailijaArvo: nimiSchema,
  })
  .nullable()

const sukunimiSchema = z
  .object({
    vtjArvo: nimiSchema,
    ilmoitettuArvo: nimiSchema,
    virkailijaArvo: nimiSchema,
  })
  .nullable()

const personSchema = z
  .object({
    personType: z.nativeEnum(TodistusPersonType),
    roles: z.array(z.nativeEnum(TodistusPersonRole)).min(1),
    hetu: z
      .string()
      .nullable()
      .refine((hetu) => hetu === null || validateHetu(hetu)),
    firstNames: etunimetSchema,
    lastName: sukunimiSchema,
    isDependant: z.boolean(),
    maritalStatus: z.null(),
    kotikuntaKoodi: z.string().nullable(), // @TODO better kotikuntakoodi. Use todistukset-common vtj-kuntakoodi.type
    citizenshipCode: z.null(),
    countryOfResidenceCode: z.null(),
    religiousCommunityTranslated: z.tuple([]),
    orderedAmount: tilaustenMaaraSchema,
    email: z.null(),
    numberOfMarriages: z.null(),
    phoneNumber: z.null(),
    eidasPID: z.string().nullable(),
    hetuSource: z.nativeEnum(TodistusPersonHetuSource).nullable(),
    birthdate: z.null(),
    birthPlace: z.null(),
    birthCountryCode: z.null(),
    asiointikieli: z.enum(availableTodistusAsiointikielet).nullable(),
    yTunnus: z.null(),
    organizationName: z.null(),
  })
  .refine((person) => person.hetu !== null || person.eidasPID !== null, {
    message: 'should_contain_hetu_or_eidaspid',
    params: { errorTranslationKey: 'asd' },
  })

export const personsSchema = z
  .array(personSchema)
  .min(1)
  .refine(
    (persons) =>
      persons.some((s) => s.roles.includes(TodistusPersonRole.TILAAJA)),
    { message: 'should_contain_tilaaja' }
  )
  .refine(
    (persons) =>
      persons.some((s) => s.roles.includes(TodistusPersonRole.KOHDEHENKILO)),
    { message: 'should_contain_kohdehenkilo' }
  )

export const validatePersons: TodistusPyyntöValueValidator = (
  value,
  form,
  user
) => {
  const propertyName: keyof ValidatorTodistusPyyntö = 'persons'

  if (!user) {
    return {
      propertyName,
      errorTranslationKey: 'validationSubjectIsMandatory',
    }
  }

  const parserResult = personsSchema.safeParse(value)
  if (!parserResult.success) {
    const { errors } = parserResult.error
    if (
      errors.some(
        matches({
          code: ZodIssueCode.invalid_type,
          path: [],
        })
      ) ||
      errors.some(matches({ code: ZodIssueCode.too_small })) ||
      errors.some(matches({ message: 'should_contain_kohdehenkilo' }))
    ) {
      return {
        propertyName,
        errorTranslationKey: 'validationSubjectIsMandatory',
      }
    }
    if (
      errors.some(
        matches({
          code: ZodIssueCode.custom,
          message: 'should_contain_tilaaja',
        })
      )
    ) {
      return {
        propertyName,
        errorTranslationKey: 'validationUnknownError',
      }
    }
    if (
      errors.some(
        matches({
          code: ZodIssueCode.custom,
          message: 'should_contain_hetu_or_eidaspid',
        })
      )
    ) {
      return {
        propertyName,
        errorTranslationKey: 'validationSubjectDataIsCorrupted',
      }
    }
    throw parserResult.error.errors[0]
  }

  if (!isValueInEnum(form.todistusType, CertificateTypes)) {
    return {
      propertyName,
      errorTranslationKey: 'validationUnknownError',
    }
  }

  const enabledFeatures = getEnabledFeatures({
    certificateType: form.todistusType,
    isAdult: user.isAdult,
  })

  const canOrderForDependants = fp.includes(
    CertificateFeature.ORDER_FOR_DEPENDANTS,
    enabledFeatures
  )

  const validDependants = canOrderForDependants
    ? fp.reject(isDependantRestricted, user.huollettavat)
    : []
  const validPersons = fp.concat<TodistuksetUser | Huollettava>(
    [user],
    validDependants
  )

  const isOnlyValidPersons = parserResult.data.every((person) =>
    hasMatchingPersons(person, validPersons)
  )

  if (!isOnlyValidPersons) {
    return {
      propertyName,
      errorTranslationKey: 'validationSubjectIsNotValid',
    }
  }

  return parserResult.data.every((person): boolean =>
    [() => personIsDependant(person.isDependant, person, user)].every((fn) =>
      fn()
    )
  )
    ? undefined
    : { propertyName, errorTranslationKey: 'validationSubjectDataIsCorrupted' }
}

const hasMatchingPersons = (
  person: UnpersistedTodistusPerson,
  validPersons: (TodistuksetUser | Huollettava)[]
): boolean => {
  const matchingPersons = validPersons.filter((validPerson) =>
    isSameHetuOrEidasPID(validPerson, person)
  )
  if (matchingPersons.length !== 1) {
    return false
  }
  const matchingPerson = matchingPersons[0]

  const keys = [
    'eidasPID',
    'hetu',
    'firstNames',
    'lastName',
  ] satisfies (keyof UnpersistedTodistusPerson)[]
  for (const key of keys) {
    if (!isNullOrSameProperty(person, matchingPerson, key)) {
      return false
    }
  }
  return true
}

const isNullOrSameProperty = (
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>,
  property: string
): boolean => {
  if (!(property in obj1)) return false
  const valueOne = isEditableField(obj1[property])
    ? // @ts-expect-error isEditableField() knows what it is.
      finalEditableFieldValue(obj1[property])
    : obj1[property]
  const valueTwo = isEditableField(obj2[property])
    ? // @ts-expect-error isEditableField() knows what it is.
      finalEditableFieldValue(obj2[property])
    : obj2[property]
  if (valueOne === null) return true
  if (!(property in obj2)) return false
  if (valueOne !== valueTwo) return false
  return true
}

const personIsDependant = (
  value: unknown,
  person: UnpersistedTodistusPerson,
  user: TodistuksetUser
): boolean => {
  const isDependant = !isSameHetuOrEidasPID(person, user)
  return value === isDependant
}

const isPaymentId = (value: unknown) => {
  return (
    value === undefined || value === null || isStringOfLength(value, 36, 36)
  )
}

export const validateOrdererExtraIdentification: TodistusPyyntöValueValidator =
  (value, _form, user) => {
    const propertyName = 'ordererExtraIdentification'
    if (isEidasIdentifiedUser(user)) {
      return isOrdererExtraIdentification(value)
        ? undefined
        : {
            propertyName,
            errorTranslationKey: 'validationInvalidDataType',
          }
    }
    return value === undefined
      ? undefined
      : {
          propertyName,
          errorTranslationKey: 'validationUnexpectedKey',
        }
  }

export const validateOrdererExtraIdentificationBirthPlace: TodistusPyyntöValueValidator =
  (value, _form, user) => {
    const propertyName = 'ordererExtraIdentification.birthPlace'
    if (isEidasIdentifiedUser(user)) {
      return validateString(propertyName, value, {
        minLength: 1,
        maxLength: 100,
        disallowedCharacters: REGEX_MAIL_DELIVERY_NOT_ALLOWED_CHARACTERS,
      })
    }
    return undefined
  }

export const validateOrdererExtraIdentificationBirthCountry: TodistusPyyntöValueValidator =
  (value, _form, user) => {
    const propertyName = 'ordererExtraIdentification.birthCountry'
    if (isEidasIdentifiedUser(user)) {
      return validateString(propertyName, value, {
        minLength: 1,
        maxLength: 100,
        disallowedCharacters: REGEX_MAIL_DELIVERY_NOT_ALLOWED_CHARACTERS,
      })
    }
    return undefined
  }

export const validateOrdererExtraIdentificationFinnishHetu: TodistusPyyntöValueValidator =
  (value, _form, user) => {
    const propertyName = 'ordererExtraIdentification.finnishHetu'
    if (isEidasIdentifiedUser(user)) {
      if (value === null || validateHetu(value)) {
        return undefined
      }
      return {
        propertyName,
        errorTranslationKey: 'validationInvalidHetu',
      }
    }
    return undefined
  }

export const validateOrdererExtraIdentificationForeignPersonId: TodistusPyyntöValueValidator =
  (value, form, user) => {
    const propertyName = 'ordererExtraIdentification.foreignPersonId'
    if (
      !(
        isEidasIdentifiedUser(user) &&
        isOrdererExtraIdentification(form.ordererExtraIdentification)
      )
    ) {
      return undefined
    }
    if (
      form.ordererExtraIdentification.foreignPersonIdCountry !== null ||
      hasOrdererExtraIdentificationOptionWithValue(
        form,
        'addForeignPersonId',
        true
      )
    ) {
      return validateString(propertyName, value, {
        minLength: 1,
        maxLength: 100,
        disallowedCharacters: REGEX_MAIL_DELIVERY_NOT_ALLOWED_CHARACTERS,
      })
    }
    return form.ordererExtraIdentification.foreignPersonId === null &&
      value === null
      ? undefined
      : {
          propertyName,
          errorTranslationKey: 'validationRequiredValueMissing',
        }
  }

export const validateOrdererExtraIdentificationForeignPersonIdCountry: TodistusPyyntöValueValidator =
  (value, form, user) => {
    const propertyName = 'ordererExtraIdentification.foreignPersonIdCountry'
    if (
      !(
        isEidasIdentifiedUser(user) &&
        isOrdererExtraIdentification(form.ordererExtraIdentification)
      )
    ) {
      return undefined
    }

    if (
      form.ordererExtraIdentification.foreignPersonId !== null ||
      hasOrdererExtraIdentificationOptionWithValue(
        form,
        'addForeignPersonId',
        true
      )
    ) {
      if (isKoodistoCountryCode(value) && value !== EuCountries.FINLAND) {
        return undefined
      }
      return {
        propertyName,
        errorTranslationKey:
          value === null
            ? 'validationOrdererExtraIdentificationMissingPersonIdCountry'
            : 'validationOrdererExtraIdentificationInvalidPersonIdCountry',
      }
    }
    return form.ordererExtraIdentification.foreignPersonId === null &&
      value === null
      ? undefined
      : {
          propertyName,
          errorTranslationKey: 'validationRequiredValueMissing',
        }
  }

export const validateOrdererExtraIdentificationPreviousName: TodistusPyyntöValueValidator =
  (value, form, user) => {
    const propertyName = 'ordererExtraIdentification.previousName'
    if (isEidasIdentifiedUser(user)) {
      return value === null &&
        !hasOrdererExtraIdentificationOptionWithValue(
          form,
          'addPreviousName',
          true
        )
        ? undefined
        : validateString(propertyName, value, {
            minLength: 1,
            maxLength: 100,
            disallowedCharacters: REGEX_MAIL_DELIVERY_NOT_ALLOWED_CHARACTERS,
          })
    }
    return undefined
  }

const isWithinBounds = (value: number, min?: number, max?: number) =>
  value >= (min ?? -Infinity) && value <= (max ?? Infinity)

const isStringOfLength = (value: unknown, min?: number, max?: number) => {
  if (typeof value !== 'string') {
    return false
  }

  if (typeof min === 'number' && value.length < min) {
    return false
  }

  if (typeof max === 'number' && value.length > max) {
    return false
  }

  return true
}

const validateString = (
  propertyName: string,
  value: unknown,
  {
    minLength,
    maxLength,
    whitelistedValues,
    normalizedWhitespace = true,
    disallowedCharacters,
  }: {
    minLength?: number
    maxLength?: number
    normalizedWhitespace?: boolean
    whitelistedValues?: unknown[]
    disallowedCharacters?: RegExp
  } = {},
  {
    isRequiredMsg = 'validationRequiredValueMissing',
    notStringMsg = isRequiredMsg,
    tooShortMsg = isRequiredMsg,
    tooLongMsg = 'validationLengthExceedsMaximum',
    invalidWhitespaceMsg = 'validationWhitespaceNotNormalized',
    invalidCharactersMsg = 'validationInvalidCharacters',
  }: {
    notStringMsg?: string
    isRequiredMsg?: string
    tooShortMsg?: string
    tooLongMsg?: string
    invalidWhitespaceMsg?: string
    invalidCharactersMsg?: string
  } = {}
): ValidationError | undefined => {
  if (Array.isArray(whitelistedValues) && whitelistedValues.includes(value)) {
    return undefined
  }

  if (typeof value !== 'string') {
    return {
      propertyName,
      errorTranslationKey: notStringMsg,
    }
  }

  if (minLength !== undefined && value.length < minLength) {
    return {
      propertyName,
      errorTranslationKey: tooShortMsg,
      errorTranslationParams: { count: minLength },
    }
  }

  if (maxLength !== undefined && value.length > maxLength) {
    return {
      propertyName,
      errorTranslationKey: tooLongMsg,
      errorTranslationParams: { count: maxLength },
    }
  }

  if (normalizedWhitespace && normalizeTextInputWhitespace(value) !== value) {
    return {
      propertyName,
      errorTranslationKey: invalidWhitespaceMsg,
    }
  }

  if (disallowedCharacters) {
    const invalidCharacters = fp.uniq(value.match(disallowedCharacters))
    if (invalidCharacters.length) {
      return {
        propertyName,
        errorTranslationKey: invalidCharactersMsg,
        errorTranslationParams: { characters: invalidCharacters.join(' ') },
      }
    }
  }

  return undefined
}

const hasSameKeys = <Keys extends string>(
  candidate: unknown,
  reference: readonly Keys[],
  optionalKeys: string[] = []
): candidate is Record<Keys, unknown> => {
  const keys = fp.keys(candidate).filter((k) => !optionalKeys.includes(k))
  if (fp.isEmpty(keys)) {
    return false
  }
  const sortByName = fp.sortBy(fp.identity)
  const ret = fp.isEqual(sortByName(keys), sortByName(reference))
  return ret
}

export const isValidPhoneNumber = (string: string): boolean =>
  /^\+?[0-9]{3,100}$/.test(removeSpaces(string))

export const removeSpaces = (string: string): string =>
  string.replace(/\s/g, '')

const hasUsageRegionKeys = (
  usageRegion: unknown
): usageRegion is HasSameKeys<UsageRegion> =>
  hasSameKeys(usageRegion, expectedUsageRegionKeys)

const isValueInNullableEnum = <EnumType extends Record<string, unknown>>(
  value: unknown,
  referenceEnum: EnumType
): value is EnumType[keyof EnumType] | null =>
  value === null || isValueInEnum(value, referenceEnum)

const isKoodistoCountryCode = (
  countryKoodistoCode: unknown
): countryKoodistoCode is CountryKoodistoCode =>
  typeof countryKoodistoCode === 'string' &&
  getValtioKoodistoEntryByCode(countryKoodistoCode) !== undefined

const isUsageRegion = (usageRegion: unknown): usageRegion is UsageRegion =>
  hasUsageRegionKeys(usageRegion) &&
  isValueInNullableEnum(usageRegion.target, UsageRegionTarget) &&
  isValueInNullableEnum(usageRegion.receiver, UsageRegionReceiver) &&
  (usageRegion.countryKoodistoCode === null ||
    isKoodistoCountryCode(usageRegion.countryKoodistoCode))

const isMailDeliveryEnabled = (
  usageRegion: unknown,
  orderer: Orderer
): boolean =>
  isUsageRegion(usageRegion) &&
  checkIfMailDeliveryIsEnabled({ usageRegion, orderer })

const isOrdererExtraIdentification = (
  value: unknown
): value is OrdererExtraIdentification => {
  if (!hasSameKeys(value, expectedOrdererExtraIdentificationKeys)) {
    return false
  }

  function isString(value: unknown): boolean {
    return typeof value === 'string'
  }

  function isNullableString(value: unknown): boolean {
    return value === null || isString(value)
  }

  function isNullableKoodistoCode(value: unknown): boolean {
    return value === null || isKoodistoCountryCode(value)
  }

  const fieldsPass: Record<keyof OrdererExtraIdentification, boolean> = {
    birthPlace: isString(value.birthPlace),
    birthCountry: isString(value.birthCountry),
    finnishHetu: isNullableString(value.finnishHetu),
    foreignPersonId: isNullableString(value.foreignPersonId),
    foreignPersonIdCountry: isNullableKoodistoCode(
      value.foreignPersonIdCountry
    ),
    previousName: isNullableString(value.previousName),
  }

  return Object.values(fieldsPass).every((pass) => pass)
}

const hasOrdererExtraIdentificationOptionWithValue = (
  form: HasSameKeys<ValidatorTodistusPyyntö>,
  option: keyof StoreUiTodistusPyyntö['ordererExtraIdentificationOptions'],
  value: boolean
): boolean => {
  if (
    'ordererExtraIdentificationOptions' in form &&
    typeof form.ordererExtraIdentificationOptions === 'object' &&
    form.ordererExtraIdentificationOptions !== null &&
    option in form.ordererExtraIdentificationOptions
  ) {
    return (
      (
        form.ordererExtraIdentificationOptions as StoreUiTodistusPyyntö['ordererExtraIdentificationOptions']
      )[option] === value
    )
  }
  return false
}
